import { type FC } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useContractPaymentsQuery } from "@/partner/entities/billing";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { interpolationFormat } from "@/partner/shared/lib";
import { ErrorMessage } from "@/shared/components";
import { formatAmountObject } from "@/partner/features/products";
import type { ContractPaymentsListQueryType } from "@/types/graphql/partner.generated";
import { buildInvoicesFilter } from "../model/buildInvoicesFilter";
import {
  contractPaymentStatusColor,
  contractPaymentStatusKey,
} from "../model/paymentStatus";

type ContractPaymentRow =
  ContractPaymentsListQueryType["contractPayments"]["nodes"][number];

export const BillingInvoices: FC = () => {
  const { t, i18n } = useTranslation();
  const {
    filter,
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage },
  } = useFilter();

  const { data, loading, error } = useContractPaymentsQuery({
    variables: { page, rowsPerPage, filter: buildInvoicesFilter(filter) },
    fetchPolicy: "cache-and-network",
  });

  const rows = data?.contractPayments?.nodes ?? [];
  const count = data?.contractPayments?.count ?? 0;

  const emptyValue = t("common.emptyDash");

  const formatDate = (value: string): string =>
    interpolationFormat(value, "short", i18n.language);

  const formatStatus = (row: ContractPaymentRow): string =>
    t(`billing.status.${contractPaymentStatusKey(row.status)}`);

  if (error) return <ErrorMessage error={error} />;

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (count === 0) {
    return <EmptyListCard title={t("billing.invoices.empty")} />;
  }

  return (
    <Card elevation={0} sx={{ overflowX: "auto" }}>
      <Table size="medium">
        <TableHead>
          <TableRow>
            <TableCell>{t("billing.invoices.columns.period")}</TableCell>
            <TableCell>{t("billing.invoices.columns.invoiceDate")}</TableCell>
            <TableCell>{t("billing.invoices.columns.dueAt")}</TableCell>
            <TableCell align="right">
              {t("billing.invoices.columns.amount")}
            </TableCell>
            <TableCell>{t("billing.invoices.columns.status")}</TableCell>
            <TableCell>{t("billing.invoices.columns.number")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id} hover>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                <Typography variant="body2" noWrap>
                  {formatDate(row.periodFrom)}, {formatDate(row.periodTo)}
                </Typography>
              </TableCell>

              <TableCell sx={{ whiteSpace: "nowrap" }}>
                <Typography variant="body2" noWrap>
                  {formatDate(row.invoiceDate)}
                </Typography>
              </TableCell>

              <TableCell sx={{ whiteSpace: "nowrap" }}>
                <Typography variant="body2" noWrap>
                  {formatDate(row.expiredAt)}
                </Typography>
                {/* Просрочка это флаг, а не статус (см. схему): подпись
                    показана отдельно, под сроком оплаты. */}
                {row.expired && (
                  <Typography variant="caption" color="error" noWrap>
                    {t("billing.invoices.overdue")}
                  </Typography>
                )}
              </TableCell>

              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                {/* У счёта в другой валюте к оплате сумма в валюте счёта,
                    ровно та, что в PDF; иначе сумма платежа. */}
                <Typography variant="body2" fontWeight={600} noWrap>
                  {formatAmountObject(row.invoiceAmount ?? row.amount)}
                </Typography>
              </TableCell>

              <TableCell>
                <Chip
                  size="small"
                  color={contractPaymentStatusColor(row.status)}
                  label={formatStatus(row)}
                />
              </TableCell>

              {/* Без выставленного счёта invoice/invoiceUrl оба null:
                  прочерк вместо ссылки, по прямому адресу файла нет. */}
              <TableCell>
                {row.invoice && row.invoiceUrl ? (
                  <Tooltip title={t("billing.invoices.download")}>
                    <MuiLink
                      href={row.invoiceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      color="secondary"
                    >
                      {row.invoice.number}
                    </MuiLink>
                  </Tooltip>
                ) : (
                  <Typography variant="body2">{emptyValue}</Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25, 100]}
        labelRowsPerPage={t("common.pagination.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          t("common.pagination.displayedRows", {
            from,
            to,
            count:
              count !== -1 ? count : t("common.pagination.moreThan", { to }),
          })
        }
      />
    </Card>
  );
};
